import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Item,
  ItemLimitation,
  MagicItemKeyword,
  PseudoItem,
  WEAPON_KEYWORD_OPTIONS, 
} from "@dndModels";
import { 
  ATTACK_TYPES, 
  DAMAGE_TYPES, 
  ITEM_TYPES, 
  RARITY_NUM_TO_STR_MAP, 
  RARITY_OPTIONS, 
} from "@dndModels/Constants";
import { 
  AttackType, 
  DamageType, 
  ItemType 
} from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CheckBox from "@components/input/CheckBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import LoadingBox from "@components/input/LoadingBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import ViewItem from "@dndComponents/display/ViewItem";
import ButtonBox from "@components/input/buttons/ButtonBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import { DisablableOption } from "@models";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import StringBox from "@components/input/strings/StringBox";

interface Props { 
  obj: ItemLimitation;
  change_time?: number;
  onChange: (changed: ItemLimitation) => void;
  onDelete?: () => void;
  disabled?: boolean;
}

const api = DandDAPI.getInstance();

export default function ItemLimitInput({ 
  obj,
  change_time = 0,
  onChange,
  onDelete, // I'll add an implementation for this later.
  disabled = false 
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [view, setView] = useState("Main");
  const [items, setItems] = useState<Item[]>([]);
  const [options, setOptions] = useState<Item[]>([]);
  const [magic_item_keywords, setMagicItemKeywords] = useState<MagicItemKeyword[]>([]);
  const [filter, setFilter] = useState<any>({ });
  const [expanded, setExpanded] = useState("");
    
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });
  
  const load = useCallback( async () => {
    const res = await api.getSetOfObjects({ data_types: ["item","magic_item_keyword"] });
    setItems(res.item as Item[]);
    setMagicItemKeywords(res.magic_item_keyword as MagicItemKeyword[]);
    setLoading(false);
    setRecalc(true);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      });
    }
  }, [fetched, load]);

  const calc_use_list = useCallback(() => {
    const the_filter = {...filter};
    let filtered_items = api.filter("", items, the_filter) as Item[];
    const ids = filtered_items.map(o => o._id);
    const filtered_miks = (api.filter("", magic_item_keywords, the_filter) as MagicItemKeyword[]).filter(o => o.min_rarity !== -1);
    const other_items = items.filter(o => !ids.includes(o._id) && 
      filtered_miks.find(o2 => o2.possible_base_item_ids.includes(o._id)));
    filtered_items = [...filtered_items,...other_items];
    setOptions(filtered_items);
  }, [filter, items, magic_item_keywords]);
  
  useEffect(() => {
    if (recalc) {
      setRecalc(false);
      setTimeout(() => {
        calc_use_list();
      });
    }
  }, [recalc, calc_use_list]);

  const updateFilter = (changed: any) => {
    const the_filter = {...changed};
    if (the_filter.effect_type) {
      the_filter.the_effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.the_attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    setFilter(the_filter);
    setRecalc(true);
  }
  
  const renderItems = () => {
    if (options.length < 100) {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { options.sort((a, b) => { 
            if (a.min_rarity !== b.min_rarity) {
              return a.min_rarity - b.min_rarity;
            }
            return a.name.localeCompare(b.name); 
          }).map(item => {
            return renderItem(item);
          })}
        </Grid>
      );
    } else {
      return (
        <Grid
          size={{
            xs: 12
          }}>Too many results ({ options.length })</Grid>
      );
    }
  }

  const renderIncludedItems = () => {
    if (obj.pseudo_item_ids.length > 0) {
      return (
        <>
          <Grid size={12} className='header'>
            Included Items
          </Grid>
          { !reloading && obj.pseudo_item_ids.map(id => {
            const pseudo_item = new PseudoItem();
            const pieces = id.split(",");
            const b_id = pieces.find(o => o.startsWith("B:"));
            const r_id = pieces.find(o => o.startsWith("R:"));
            const k_id = pieces.find(o => o.startsWith("K:"));
            if (b_id) {
              const base_item = items.find(o => b_id.includes(o._id));
              if (base_item) {
                pseudo_item.base_item = base_item;
              }
            }
            if (r_id) {
              const r = +r_id.substring(2);
              if (!isNaN(r)) {
                pseudo_item.selected_rarity = r;
              }
            }
            if (k_id) {
              const mik = magic_item_keywords.find(o => k_id.includes(o._id));
              if (mik) {
                pseudo_item.magic_item_keywords.push(mik);
              }
            }
            return (
              <Grid size={12} key={id}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    size={{
                      xs: 9
                    }}>{ pseudo_item.name }</Grid>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <DeleteButton
                      disabled={disabled}
                      onClick={() => {
                        const new_obj = obj;
                        new_obj.pseudo_item_ids = new_obj.pseudo_item_ids.filter(o => o !== id);
                        onChange(new_obj);
                        setReloading(true);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  }

  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  const renderItem = (item: Item) => {
    const is_expanded = expanded === item._id;
    // if (item instanceof PseudoItem && item.name.includes("Amethyst")) {
    //   debugger;
    // }
    const rarity_options: DisablableOption[] = [];
    if (item.min_rarity < item.max_rarity) {
      for (let i = item.min_rarity; i <= item.max_rarity; ++i) {
        const opt: DisablableOption = { id: `${i}`, text: RARITY_NUM_TO_STR_MAP[i] };
        rarity_options.push(opt);
      }
    }
    const possible_mik_ids = rarity_options.length === 0 ? item.possible_mik_ids : [];
    const possible_miks = magic_item_keywords.filter(o => possible_mik_ids.includes(o._id));
    return (
      <Grid size={12} className="padded" key={item._id}>
        <Grid container spacing={0} direction="row"
          className={cx2(rowClasses.root)}>
          <Grid
            container
            spacing={0}
            direction="row"
            className="clickable"
            onClick={() => {
                setExpanded(is_expanded ? "" : item._id);
              }}
            size={{
              xs: 8
            }}>
            <Grid size={12}>
              <ViewItem item={item} />
            </Grid>
            { possible_miks.length > 0 &&
              <Grid size={12} className="small_label gray">
                { possible_miks.map(mik => mik.use_name).join(", ") }
              </Grid>
            }
          </Grid>
          <Grid
            style={{ margin: "auto" }}
            size={{
              xs: 4
            }}>
            <ButtonBox
              name="Add"
              small
              onClick={() => {
                const new_obj = obj;
                new_obj.pseudo_item_ids.push(`B:${item._id}`);
                onChange(new_obj);
                setReloading(true);
              }}
            />
          </Grid>
          { is_expanded &&
            <>
              <Grid size={12}>
                <DisplayStringWithReplacements display_string={item.description} />
              </Grid>
              { rarity_options.map(opt => {
                return (
                  <Grid size={12} key={opt.id}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 10
                        }}>{ opt.text }</Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <ButtonBox
                          name="Add item with Rarity"
                          disabled={disabled}
                          onClick={() => {
                            const new_obj = obj;
                            new_obj.pseudo_item_ids.push(`B:${item._id},R:${opt.id}`);
                            onChange(new_obj);
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
              { possible_miks.map(mik => {
                return (
                  <Grid size={12} key={mik._id}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 10
                        }}>{ mik.use_name }</Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <ButtonBox
                          name="Add item with Keyword"
                          disabled={disabled}
                          onClick={() => {
                            const new_obj = obj;
                            new_obj.pseudo_item_ids.push(`B:${item._id},K:${mik._id}`);
                            onChange(new_obj);
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </>
          }
        </Grid>
      </Grid>
    );
  }

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (prev_change_time !== change_time) {
      setChangeTime(change_time);
    }
  }, [prev_change_time, change_time, setChangeTime]);
  
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <CenteredMenu
            options={["Main","Specific Items"]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { view === "Main" ? 
          <>
            <Grid
              size={{
                xs: obj.item_types.length > 0 ? 9 : 12
              }}>
              <SelectStringBox 
                name="Item Types" 
                options={ITEM_TYPES}
                values={obj.item_types} 
                multiple
                disabled={disabled}
                onChange={(changed: ItemType[]) => {
                  const new_obj = obj;
                  new_obj.item_types = changed;
                  new_obj.weapon_keyword_ids = [];
                  new_obj.excluded_keyword_ids = [];
                  new_obj.mik_ids = [];
                  new_obj.excluded_mik_ids = [];
                  if (new_obj.item_types.length === 0) {
                    new_obj.exclude_item_types = false;
                  }
                  onChange(new_obj);
                  setReloading(true);
                }} 
              />
            </Grid>
            { obj.item_types.length > 0 &&
              <Grid size={3}>
                <CheckBox 
                  name="Exclude" 
                  value={obj.exclude_item_types}
                  disabled={disabled}
                  onChange={(changed: boolean) => {
                    const new_obj = obj;
                    new_obj.exclude_item_types = changed;
                    new_obj.weapon_keyword_ids = [];
                    new_obj.excluded_keyword_ids = [];
                    new_obj.mik_ids = [];
                    new_obj.excluded_mik_ids = [];
                    
                    onChange(new_obj);
                    setReloading(true);
                  }} 
                />
              </Grid>
            }
            { obj.item_types.length === 1 && obj.item_types[0] === "Weapon" && !obj.exclude_item_types &&
              <>
                <Grid size={6}>
                  <SelectStringBox 
                    name="Weapon Keywords" 
                    options={WEAPON_KEYWORD_OPTIONS}
                    extra_options={["Any","Any Weapon","Unarmed Strike"]}
                    values={obj.weapon_keyword_ids} 
                    multiple
                    disabled={disabled}
                    onChange={(values: string[]) => {
                      const new_obj = obj;
                      new_obj.weapon_keyword_ids = values;
                      onChange(new_obj);
                    }} 
                  />
                </Grid>
                <Grid size={6}>
                  <SelectStringBox 
                    name="Excluded Weapon Keywords" 
                    options={WEAPON_KEYWORD_OPTIONS}
                    extra_options={["Any","Any Weapon","Unarmed Strike"]}
                    values={obj.excluded_keyword_ids} 
                    multiple
                    disabled={disabled}
                    onChange={(values: string[]) => {
                      const new_obj = obj;
                      new_obj.excluded_keyword_ids = values;
                      onChange(new_obj);
                    }} 
                  />
                </Grid>
              </>
            }
            <Grid size={6}>
              <SelectObjectBox 
                name="Magic Item Keywords" 
                data_type="magic_item_keyword"
                values={obj.mik_ids} 
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.mik_ids = changed;
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={6}>
              <SelectObjectBox 
                name="Excluded Magic Item Keywords" 
                data_type="magic_item_keyword"
                values={obj.excluded_mik_ids} 
                multiple
                disabled={disabled}
                onChange={(changed: string[]) => {
                  const new_obj = obj;
                  new_obj.excluded_mik_ids = changed;
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={4}>
              <SelectStringBox
                name="Minimum Rarity"
                options={RARITY_OPTIONS}
                value={`${obj.min_rarity}`}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.min_rarity = +changed;
                  if (new_obj.max_rarity < new_obj.min_rarity) {
                    new_obj.max_rarity = new_obj.min_rarity;
                  }
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={4}>
              <SelectStringBox
                name="Maximum Rarity"
                options={RARITY_OPTIONS}
                value={`${obj.max_rarity}`}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.max_rarity = +changed;
                  if (new_obj.max_rarity < new_obj.min_rarity) {
                    new_obj.min_rarity = new_obj.max_rarity;
                  }
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={4}>
              <SelectStringBox
                name="Magical"
                options={["Any","Yes","No"]}
                value={ obj.magical === null ? "Any" : (obj.magical ? "Yes" : "No") }
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.magical = changed === "Any" ? null : changed === "Yes";
                  
                  onChange(new_obj);
                }}
              />
            </Grid>
            <Grid size={6}>
              <SelectStringBox 
                name="Attack Types" 
                options={ATTACK_TYPES}
                values={obj.attack_types} 
                multiple
                disabled={disabled}
                onChange={(changed: AttackType[]) => {
                  const new_obj = obj;
                  new_obj.attack_types = changed;
                  
                  onChange(new_obj);
                }} 
              />
            </Grid>
            <Grid size={6}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <SelectStringBox 
                    name="Damage Types" 
                    options={DAMAGE_TYPES}
                    // extra_options={["Selectable"]}
                    values={obj.damage_types} 
                    multiple
                    disabled={disabled}
                    onChange={(changed: (DamageType | "Selectable")[]) => {
                      const new_obj = obj;
                      new_obj.damage_types = changed;
                      
                      onChange(new_obj);
                    }} 
                  />
                </Grid>
                { obj.damage_types.includes("Selectable") && 
                  <Grid size={12}>
                    <StringBox 
                      name="Selectable Key" 
                      value={obj.damage_selectable_key} 
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        new_obj.damage_selectable_key = changed;
                        
                        onChange(new_obj);
                      }} 
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
          </>
        :
          <>
            <Grid size={12}>
              <FilterBox 
                data_type="item"
                flat
                dont_use_api
                all_objects={items}
                the_filter={{ search_string: [""] }}
                onChange={updateFilter}
              />
            </Grid>
            { renderItems() }
            { renderIncludedItems() }
          </>
        }
      </Grid>
    );
   }
}
